import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar,
  Badge,
  Button,
  EmptyState,
  Icon,
  Input,
  Sheet,
  Skeleton,
} from '@astral/ui';
import type { ChannelDTO } from '@astral/shared';
import { openTelegramLink } from '@astral/telegram/webapp';
import { api, ApiClientError } from '../lib/api.js';
import { useAuth } from '../providers/AuthProvider.js';
import { useT } from '../providers/I18nProvider.js';
import { useToast } from '../providers/ToastProvider.js';
import { useChannels } from '../hooks/useChannels.js';
import { ConfirmSheet } from '../components/ConfirmSheet.js';

/** "My Channels" management. Viewing is open; managing needs channel:manage. */
export function ChannelsScreen(): JSX.Element {
  const t = useT();
  const toast = useToast();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canManage = hasPermission('channel:manage');
  const { channels, loading, error, reload } = useChannels();

  const [editing, setEditing] = useState<ChannelDTO | null>(null);
  const [creating, setCreating] = useState(false);
  const [removing, setRemoving] = useState<ChannelDTO | null>(null);
  const [busy, setBusy] = useState(false);

  const open = (channel: ChannelDTO): void => openTelegramLink(channel.url);

  const remove = (): void => {
    if (!removing) return;
    setBusy(true);
    void api
      .deleteChannel(removing.id)
      .then(() => {
        setRemoving(null);
        reload();
      })
      .finally(() => setBusy(false));
  };

  return (
    <div className="app-root">
      <AppBar
        title={t('channels.title')}
        onBack={() => navigate(-1)}
        trailing={
          canManage && (
            <button className="icon-btn" aria-label={t('channels.add')} onClick={() => setCreating(true)}>
              <Icon name="plus" size={20} />
            </button>
          )
        }
      />

      <div className="screen">
        <p className="muted" style={{ fontSize: 13, marginTop: -4 }}>{t('channels.subtitle')}</p>
        {loading ? (
          <div className="stack">
            <Skeleton height={64} radius={16} />
            <Skeleton height={64} radius={16} />
          </div>
        ) : error ? (
          <EmptyState
            icon={<Icon name="sparkles" size={26} />}
            title={t('errors.loadFailed')}
            action={<Button onClick={reload}>{t('common.retry')}</Button>}
          />
        ) : channels.length === 0 ? (
          <EmptyState
            icon={<Icon name="sparkles" size={26} />}
            title={t('channels.empty')}
            action={
              canManage && (
                <Button leftIcon={<Icon name="plus" size={18} />} onClick={() => setCreating(true)}>
                  {t('channels.add')}
                </Button>
              )
            }
          />
        ) : (
          <div className="stack">
            {channels.map((channel) => (
              <div key={channel.id} className="channel-row border-glow">
                <button className="channel-row__main" onClick={() => open(channel)}>
                  <span className="channel-row__avatar">
                    <Icon name="sparkles" size={18} />
                  </span>
                  <span className="channel-row__text">
                    <span className="channel-row__name">
                      {channel.name ?? `@${channel.username}`}
                    </span>
                    <span className="channel-row__handle">@{channel.username}</span>
                    {channel.description && <span className="channel-desc">{channel.description}</span>}
                  </span>
                  {(channel.category || channel.label) && (
                    <Badge tone="accent">{channel.category ?? channel.label}</Badge>
                  )}
                  <Icon name="chevron-right" size={18} />
                </button>
                {canManage && (
                  <div className="channel-row__actions">
                    <button className="icon-btn icon-btn--sm" aria-label={t('common.edit')} onClick={() => setEditing(channel)}>
                      <Icon name="edit" size={16} />
                    </button>
                    <button
                      className="icon-btn icon-btn--sm icon-btn--danger"
                      aria-label={t('common.remove')}
                      onClick={() => setRemoving(channel)}
                    >
                      <Icon name="trash" size={16} />
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {(creating || editing) && (
        <ChannelForm
          channel={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            setCreating(false);
            setEditing(null);
            reload();
          }}
          onError={(msg) => toast.show(msg, 'error')}
        />
      )}

      <ConfirmSheet
        open={Boolean(removing)}
        danger
        loading={busy}
        title={t('channels.removeConfirmTitle')}
        message={t('channels.removeConfirmBody', { name: removing?.name ?? `@${removing?.username ?? ''}` })}
        confirmLabel={t('common.remove')}
        onConfirm={remove}
        onCancel={() => setRemoving(null)}
      />
    </div>
  );
}

/** Add/edit channel bottom sheet. */
function ChannelForm({
  channel,
  onClose,
  onSaved,
  onError,
}: {
  channel: ChannelDTO | null;
  onClose: () => void;
  onSaved: () => void;
  onError: (message: string) => void;
}): JSX.Element {
  const t = useT();
  const [username, setUsername] = useState(channel?.username ?? '');
  const [name, setName] = useState(channel?.name ?? '');
  const [label, setLabel] = useState(channel?.label ?? '');
  const [description, setDescription] = useState(channel?.description ?? '');
  const [category, setCategory] = useState(channel?.category ?? '');
  const [saving, setSaving] = useState(false);

  const submit = async (): Promise<void> => {
    setSaving(true);
    try {
      if (channel) {
        await api.updateChannel(channel.id, {
          username: username.trim(),
          name: name.trim() || null,
          label: label.trim() || null,
          description: description.trim() || null,
          category: category.trim() || null,
        });
      } else {
        await api.createChannel({
          username: username.trim(),
          name: name.trim() || undefined,
          label: label.trim() || undefined,
          description: description.trim() || undefined,
          category: category.trim() || undefined,
        });
      }
      onSaved();
    } catch (err) {
      onError(err instanceof ApiClientError ? err.message : t('errors.generic'));
      setSaving(false);
    }
  };

  return (
    <Sheet
      open
      onClose={onClose}
      title={channel ? t('channels.editTitle') : t('channels.addTitle')}
      footer={
        <Button fullWidth size="lg" loading={saving} onClick={() => void submit()}>
          {channel ? t('common.save') : t('common.add')}
        </Button>
      }
    >
      <div className="stack">
        <Input
          label={t('channels.usernameLabel')}
          placeholder={t('channels.usernamePlaceholder')}
          value={username}
          onChange={(event) => setUsername(event.target.value)}
        />
        <Input
          label={t('channels.nameLabel')}
          aside={t('common.optional')}
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
        <Input
          label={t('channels.descriptionLabel')}
          aside={t('common.optional')}
          placeholder={t('channels.descriptionPlaceholder')}
          value={description}
          onChange={(event) => setDescription(event.target.value)}
        />
        <Input
          label={t('channels.categoryLabel')}
          aside={t('common.optional')}
          value={category}
          onChange={(event) => setCategory(event.target.value)}
        />
        <Input
          label={t('channels.labelLabel')}
          aside={t('common.optional')}
          value={label}
          onChange={(event) => setLabel(event.target.value)}
        />
      </div>
    </Sheet>
  );
}
